import { getNavigationRoute } from "~/utils/urlHelper";

const stateName = "initialRoute";

/**
 * 起動時の route.fullpath を保存しておく。初回起動時の検索パラメータ設定などにも利用する予定
 * EntraID のログインで他サイトにリダイレクトするため sessionStorage への保存も行う
 */
const mainRoute = getNavigationRoute("CIRCLE_LIST") ?? "/";
const loginRoute = getNavigationRoute("LOGIN");

type InitialRouteStateType = {
  /**
   * 値が設定された時刻
   */
  updatedAt: number
  requestURL: string
  routeFullPath: string
  routeHash: string
};
const emptyState: InitialRouteStateType = {
  updatedAt: 0,
  requestURL: "",
  routeFullPath: "",
  routeHash: ""
};

const sessionStorage = () => {
  if (import.meta.client) {
    return window.sessionStorage;
  }
  return null;
};

/**
 * sessionStorage があり 有効であればその値
 * sessionStorage があり 中身が無効であれば emptyState として扱う
 * sessionStorage がない (SSR/prerender) 時は undefined
 */
const getFromSessionStorage = (): InitialRouteStateType | undefined => {
  const storage = sessionStorage();
  if (!storage) {
  // sessionStorage がない (SSR/prerender) 時は undefined
    return undefined;
  }
  const sessionState = storage?.getItem(stateName);
  if (sessionState) {
    try {
      return JSON.parse(sessionState) as InitialRouteStateType;
    }
    catch {
      ;
    }
  }
  return emptyState;
};

export const useInitialRouteState = () => {
  const state = useState<InitialRouteStateType>(
    stateName, () => emptyState
  );

  const clearInitialRouteFullpath = () => {
    sessionStorage()?.removeItem(stateName);
    state.value = emptyState;
    return emptyState;
  };

  const setInitialRouteState = (initialRoute: InitialRouteStateType): InitialRouteStateType => {
    // console.log("setInitialRouteState", initialRoute);
    sessionStorage()?.setItem(stateName, JSON.stringify(initialRoute));
    return state.value = initialRoute;
  };

  const getInitialRoute = () => {
    switch (state.value.routeFullPath) {
      case "":
        return "";
      case "/":
      case loginRoute:
        return mainRoute;
      default:
        return state.value.routeFullPath;
    }
  };

  const getUrlAfterLogin = () => state.value.requestURL;

  const setInitialRouteFullpath = (requestURL: URL, route: { fullPath: string, hash: string }) => {
    const routeStateInSessionStorage = getFromSessionStorage();
    if (routeStateInSessionStorage === undefined) {
      // SessionStorage が取れない (SSR/prenender) 時は設定しない。クライアントサイドでのみ設定する
      // updatedAt を設定するタイミングを クライアント側でのみ実施したいため
      return false;
    }
    setInitialRouteState({
      updatedAt: (new Date()).getTime(),
      requestURL: requestURL.toString(),
      routeFullPath: routeStateInSessionStorage.updatedAt > 0
        ? routeStateInSessionStorage.routeFullPath // SessionStorage に 有効な値が存在する場合は引き継ぐ
        : route.fullPath,
      routeHash: route.hash
    });
    return true;
  };

  return {
    /**
     * 起動時の route.fullpath を保存しておく。初回起動時の検索パラメータ設定などにも利用する予定
     * すでに SessionStorage に値が存在する場合は再設定しない
     * この時 false を返す
     */
    setInitialRouteFullpath,
    clearInitialRouteFullpath,
    getInitialRoute,
    /**
     * auth2.circle.ms のログイン画面にリダイレクトする際に利用する URL
     */
    getUrlAfterLogin,
    getInitialHash: () => state.value.routeHash,
    clearInitialHash: () => state.value.routeHash = "",
  };
};
